import { classNames, setNationalParticipantBorder, setParticipantBorder } from '@sportal365/smp-widget-utils';
import { useMemo } from 'react';
import { HORIZONTAL, HORIZONTAL_REVERSE, VERTICAL } from '../../../../constants/sportEntityVariant';
import { AGG, PEN } from '../../../../constants/winnerReasons';
import { IBaseTeam, IConfigEntityLinks, INormalizedKnockoutMatch, WinnerReason } from '../../../../types';
import { SMPImage, SMPSportEntity, SMPText, SMPTooltip, StyledSMPImageVariants, StyledSMPTextVariants } from '../../../index';
import { validForCustomSportsStyles } from '../../../../constants/sportType';

type ISingleEventTeamProps = {
  teamName?: string | null;
  teamLogo?: string | null;
  teamLink?: string;
  team?: IBaseTeam;
  sportEntityVariant?: string;
  handleTeamClick?: (team: IBaseTeam) => void;
  entityLinks?: IConfigEntityLinks | null;
  urlTarget?: string;
  imageVariant?: StyledSMPImageVariants['variant'];
  winnerId?: string;
  winnerReason?: WinnerReason | null;
  penaltyShootout?: boolean;
  isMobile?: boolean;
  aggregateWinnerId?: string | null;
  teamNamePreWrap?: boolean;
  isWinner?: boolean;
  textVariant?: StyledSMPTextVariants['variant'];
  isNational?: boolean;
  scoreDetails?: Pick<INormalizedKnockoutMatch, 'aggregateScore' | 'penaltyShootoutScore'> & {
    homeTeam?: { id: string; name: string };
    awayTeam?: { id: string; name: string };
  };
  sport?: string;
  showAggEvents?: (show: boolean) => void;
  showAggEventsList?: boolean;
};

const getVariant = (
  sportEntityVariant: ISingleEventTeamProps['sportEntityVariant'],
  isMobile: ISingleEventTeamProps['isMobile'],
):
  | 'vertical'
  | {
      '@initial': 'vertical';
      '@bp0'?: 'horizontal';
      '@bp1'?: 'horizontal' | 'horizontalReverse';
    } => {
  if (isMobile) {
    return VERTICAL;
  }

  if (sportEntityVariant === 'homeTeam') {
    return { '@initial': VERTICAL, '@bp1': HORIZONTAL_REVERSE };
  }

  if (sportEntityVariant === 'awayTeam') {
    return { '@initial': VERTICAL, '@bp1': HORIZONTAL };
  }

  return { '@initial': VERTICAL, '@bp0': HORIZONTAL };
};

const toggleTooltip = (e: React.PointerEvent<HTMLSpanElement>, show: boolean) => {
  if (e.target instanceof HTMLElement === false) {
    return;
  }

  const winnerBadge = (e.target as HTMLElement).closest('.winner-badge');

  if (!winnerBadge) {
    return;
  }

  const winnerBadgeTooltip = winnerBadge.nextElementSibling;

  if (winnerBadgeTooltip instanceof HTMLDivElement && winnerBadgeTooltip.classList.contains('winner-badge-tooltip')) {
    try {
      winnerBadgeTooltip.style.transform = '';
      winnerBadgeTooltip.style.display = show ? 'inline-block' : 'none';

      const widget = winnerBadgeTooltip.closest('[data-widget-id]') || winnerBadgeTooltip.getRootNode().firstChild;

      if (widget) {
        const winnerBadgeTooltipRect = winnerBadgeTooltip.getBoundingClientRect();
        const widgetRect = (widget as HTMLElement).getBoundingClientRect();

        if (winnerBadgeTooltipRect.right > widgetRect.right) {
          winnerBadgeTooltip.style.transform = `translateX(calc(-50% + -${winnerBadgeTooltipRect.right - widgetRect.right}px))`;
        }
      }
    } catch (e) {
      console.error(e);
    }
  }
};

export const GameTeam = ({
  teamName,
  teamLogo,
  team,
  handleTeamClick,
  sportEntityVariant,
  teamLink,
  urlTarget,
  imageVariant,
  winnerId,
  winnerReason,
  isMobile,
  teamNamePreWrap,
  aggregateWinnerId,
  isWinner,
  textVariant,
  isNational,
  scoreDetails,
  sport,
  showAggEvents,
  showAggEventsList,
}: ISingleEventTeamProps): JSX.Element => {
  const handleBadgeClick = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    showAggEvents && showAggEvents(!showAggEventsList);
  };

  const winnerKnockoutBadge = useMemo(() => {
    if (!isWinner) {
      // If the team is not the winner, do not display the badge
      return null;
    }

    return (
      <div style={{ position: 'relative' }} onClick={handleBadgeClick}>
        <SMPText
          className='winner-badge'
          size='2'
          css={{
            display: 'inline-flex',
            gap: '$1',
            br: '$1',
            p: '$1',
            backgroundColor: '$statusBadgeBgColor',
            color: '$statusBadgeTextColor',
            userSelect: 'none',
          }}
          onClick={(e) => e.preventDefault()}
        >
          {AGG.toUpperCase()}
          <span>✓</span>
        </SMPText>
      </div>
    );
  }, [team?.id, showAggEventsList, winnerId]);

  const winnerBadge = useMemo(() => {
    const teamId = team?.id.toString();
    if (!(winnerReason === AGG && aggregateWinnerId === teamId) && !(winnerReason === PEN && winnerId === teamId)) {
      // If the team is not the winner, do not display the badge
      return null;
    }

    /*
     * If showAdditionalInfo is true, display a checkmark next to the winner reason and a tooltip with the score details on hover
     * (Currently only used in knockout matches)
     */
    const showAdditionalInfo = !!scoreDetails;
    return (
      <div style={{ position: 'relative' }}>
        <SMPText
          className='winner-badge'
          size='2'
          css={{
            display: 'inline-flex',
            gap: '$1',
            br: '$1',
            p: '$1',
            backgroundColor: '$statusBadgeBgColor',
            color: '$statusBadgeTextColor',
            userSelect: 'none',
          }}
          onClick={(e) => e.preventDefault()}
          onPointerEnter={(e) => toggleTooltip(e, true)}
          onPointerLeave={(e) => toggleTooltip(e, false)}
        >
          {winnerReason?.toUpperCase()}
          {showAdditionalInfo && <span>✓</span>}
        </SMPText>
        {showAdditionalInfo && (
          <SMPTooltip className='winner-badge-tooltip'>
            <span style={{ fontWeight: (winnerId === scoreDetails.homeTeam?.id && 'bold') || undefined }}>{`${
              scoreDetails.homeTeam?.name
            } ${scoreDetails[winnerReason === 'agg' ? 'aggregateScore' : 'penaltyShootoutScore']?.home ?? '0'}`}</span>
            {' - '}
            <span style={{ fontWeight: (winnerId === scoreDetails.awayTeam?.id && 'bold') || undefined }}>{`${
              scoreDetails[winnerReason === 'agg' ? 'aggregateScore' : 'penaltyShootoutScore']?.away ?? '0'
            } ${scoreDetails.awayTeam?.name}`}</span>
          </SMPTooltip>
        )}
      </div>
    );
  }, [aggregateWinnerId, scoreDetails, team?.id, winnerId, winnerReason]);

  return (
    <div onClick={() => handleTeamClick && team && handleTeamClick(team)}>
      <SMPSportEntity href={teamLink} target={urlTarget} variant={getVariant(sportEntityVariant, isMobile)}>
        <SMPImage
          variant={imageVariant ?? 'livescoreEntityLogo'}
          src={teamLogo ?? ''}
          css={isNational ? setNationalParticipantBorder(true) : setParticipantBorder(team, teamLogo)}
        />
        <SMPText
          className={classNames({
            'with-ellipsis': !isMobile || !!winnerBadge,
            'with-winner-badge': !!winnerBadge,
          })}
          css={{
            '@bp0': { maxWidth: '100%', whiteSpace: 'pre-wrap' },
            '@bp2': { maxWidth: '100%', whiteSpace: isMobile || teamNamePreWrap ? 'pre-wrap' : 'nowrap' },
            py: '$0',
          }}
          style={{
            color:
              !isWinner && validForCustomSportsStyles.includes(sport || '')
                ? showAggEventsList
                  ? 'var(--colors-gray4)'
                  : 'var(--colors-teamLostColor)'
                : validForCustomSportsStyles.includes(sport || '') && showAggEventsList
                ? 'var(--colors-gray0)'
                : undefined,
          }}
          variant={textVariant ?? 'contrast'}
          size={{ '@initial': '3' }}
          weight={(winnerId && winnerId === team?.id.toString()) || isWinner ? '700' : '400'}
        >
          {teamName}
        </SMPText>
        {validForCustomSportsStyles.includes(sport || '') ? (showAggEventsList ? undefined : winnerKnockoutBadge) : winnerBadge}
      </SMPSportEntity>
    </div>
  );
};
